// In / tải vận đơn hàng loạt: chọn khoảng ngày tạo đơn (từ – đến). Tải → một file zip, mỗi PDF 50 vận đơn (mỗi
// trang một tem) đúng thứ tự tạo đơn (cũ trước) + _danh-sach.csv; In → mở hộp in, mỗi lần tới 500 vận đơn
// (lib/printWaybills). Không giới hạn số vận đơn. "Chỉ vận đơn chưa tải" (bật sẵn) bỏ vận đơn đã tải / đã in;
// xong máy chủ ghi các vận đơn đó là "đã tải".

import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Download, Loader2, Printer } from "lucide-react"
import { printWaybills } from "@/lib/printWaybills"
import { downloadFile, get } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { presetDateRange, rangeSummary } from "@shoptrack/common/date-range"
import { STATUS_LABEL } from "@/lib/orderStatus"

type WaybillStatus = "active" | "cho-lay"

interface WaybillRow {
  code: string
  orderId: string
  clientOrderId: string | null
  createdAt: string
  status: string
  customerName: string | null
  customerPhone: string | null
  address: string
  weightGram: number | null
  cod: number
  downloadedAt: string | null
}

export function WaybillZipDialog({ open, onClose, initialFrom, initialTo }: { open: boolean; onClose: () => void; initialFrom?: string; initialTo?: string }) {
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [status, setStatus] = useState<WaybillStatus>("active")
  const [onlyUnprinted, setOnlyUnprinted] = useState(true)
  // Tải gì: PDF vận đơn (zip) · chỉ Excel mã vận đơn · cả hai
  const [what, setWhat] = useState<"pdf" | "excel" | "both">("pdf")
  // Đang chuẩn bị: in hay tải
  const [busy, setBusy] = useState<"print" | "download" | null>(null)
  const downloading = busy != null
  const qc = useQueryClient()

  // Mở hộp: lấy khoảng ngày đang lọc ở danh sách; chưa lọc → 7 ngày gần nhất
  useEffect(() => {
    if (!open) return
    const def = presetDateRange("7d")
    setFrom(initialFrom ?? def.from)
    setTo(initialTo ?? def.to)
  }, [open, initialFrom, initialTo])

  const qs = new URLSearchParams({ status, unprinted: onlyUnprinted ? "1" : "0", ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString()
  const count = useQuery({
    queryKey: ["waybill-zip-count", qs],
    queryFn: () => get<{ count: number; total: number; printed: number }>(`/shipping/waybills/count?${qs}`),
    enabled: open,
  })
  const n = count.data?.count ?? 0
  // ~1,2 giây / lô 50 vận đơn, máy chủ tải 3 lô cùng lúc
  const etaSec = Math.max(3, Math.ceil(Math.ceil(n / 50) / 3) * 2)

  /** File Excel danh sách mã vận đơn (ô mã là CHỮ — Excel không đổi thành 9.998E+14). Không gọi BEST. */
  async function downloadExcel(span: string) {
    const [{ waybills }, XLSX] = await Promise.all([
      get<{ waybills: WaybillRow[] }>(`/shipping/waybills/list?${qs}`),
      import("xlsx"),
    ])
    const header = ["STT", "Mã vận đơn", "Mã đơn", "Mã đơn của shop", "Khách hàng", "SĐT", "Địa chỉ", "Cân (kg)", "COD", "Trạng thái", "Ngày tạo", "Đã tải PDF lúc"]
    const rows = waybills.map((w, i) => [
      i + 1,
      w.code,
      w.orderId,
      w.clientOrderId ?? "",
      w.customerName ?? "",
      w.customerPhone ?? "",
      w.address,
      w.weightGram != null ? w.weightGram / 1000 : "",
      w.cod,
      STATUS_LABEL[w.status]?.text ?? w.status,
      new Date(w.createdAt).toLocaleString("vi-VN"),
      w.downloadedAt ? new Date(w.downloadedAt).toLocaleString("vi-VN") : "",
    ])
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
    // Mã vận đơn / SĐT luôn là chữ
    for (let r = 1; r <= rows.length; r++) {
      for (const c of [1, 5]) {
        const ref = XLSX.utils.encode_cell({ r, c })
        if (ws[ref]) ws[ref] = { t: "s", v: String(ws[ref].v) }
      }
    }
    ws["!cols"] = [6, 18, 30, 16, 22, 13, 50, 9, 11, 14, 19, 19].map((wch) => ({ wch }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Van_don")
    XLSX.writeFile(wb, `van-don_${span}.xlsx`)
  }

  async function print() {
    setBusy("print")
    try {
      // Danh sách cùng bộ lọc, cùng thứ tự với file zip
      await printWaybills(async () => (await get<{ waybills: WaybillRow[] }>(`/shipping/waybills/list?${qs}`)).waybills.map((w) => w.code))
      qc.invalidateQueries({ queryKey: ["orders"] })
      qc.invalidateQueries({ queryKey: ["order-counts"] })
      qc.invalidateQueries({ queryKey: ["waybill-zip-count"] })
      onClose()
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function download() {
    setBusy("download")
    try {
      const span = [from, to].filter(Boolean).join("_") || "tat-ca"
      if (what !== "pdf") await downloadExcel(span)
      if (what !== "excel") {
        await downloadFile(`/shipping/waybills.zip?${qs}`, `van-don_${span}.zip`)
        qc.invalidateQueries({ queryKey: ["orders"] })
        qc.invalidateQueries({ queryKey: ["order-counts"] })
      }
      toast.success(what === "excel" ? `Đã tải Excel ${n} mã vận đơn` : what === "both" ? `Đã tải ${n} vận đơn (PDF + Excel)` : `Đã tải ${n} vận đơn`)
      onClose()
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(null)
    }
  }


  return (
    <Dialog open={open} onOpenChange={(v) => !v && !downloading && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Tải/In vận đơn</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-ink">Từ ngày</span>
              <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-ink">Đến ngày</span>
              <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>

          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium text-ink">Tải gì</legend>
            {(
              [
                ["pdf", "PDF vận đơn"],
                ["excel", "Excel mã vận đơn"],
                ["both", "Cả hai"],
              ] as const
            ).map(([v, label]) => (
              <label key={v} className="flex min-h-11 cursor-pointer items-center gap-2">
                <input type="radio" name="waybill-what" className="size-4 accent-accent" checked={what === v} onChange={() => setWhat(v)} />
                {label}
              </label>
            ))}
          </fieldset>

          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium text-ink">Vận đơn</legend>
            <label className="flex min-h-11 cursor-pointer items-center gap-2">
              <input type="radio" name="waybill-status" className="size-4 accent-accent" checked={status === "active"} onChange={() => setStatus("active")} />
              Tất cả
            </label>
            <label className="flex min-h-11 cursor-pointer items-center gap-2">
              <input type="radio" name="waybill-status" className="size-4 accent-accent" checked={status === "cho-lay"} onChange={() => setStatus("cho-lay")} />
              Chỉ đơn chờ hãng lấy hàng
            </label>
          </fieldset>

          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-accent" checked={onlyUnprinted} onChange={(e) => setOnlyUnprinted(e.target.checked)} />
            Chỉ vận đơn <b>chưa tải</b>
            {count.data && count.data.printed > 0 && <span className="text-muted">(đã tải {count.data.printed.toLocaleString("vi-VN")} / {count.data.total.toLocaleString("vi-VN")})</span>}
          </label>

          <p className="rounded-md bg-paperSoft px-3 py-2 text-sm text-ink" aria-live="polite">
            {count.isLoading ? (
              "Đang đếm vận đơn…"
            ) : count.isError ? (
              <span className="text-danger">{(count.error as Error).message}</span>
            ) : n === 0 ? (
              <>Không có vận đơn {onlyUnprinted ? "chưa tải " : ""}nào {from || to ? `(${rangeSummary(from, to)})` : ""}.</>
            ) : (
              <>
                <b>{n.toLocaleString("vi-VN")}</b> vận đơn{from || to ? ` · ${rangeSummary(from, to)}` : ""} → {Math.ceil(n / 50)} file PDF. Chuẩn bị mất khoảng {etaSec} giây.
              </>
            )}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={downloading}>
            Đóng
          </Button>
          <Button variant={what === "excel" ? "default" : "outline"} onClick={download} disabled={downloading || count.isLoading || n === 0}>
            {busy === "download" ? <Loader2 className="animate-spin" aria-hidden /> : <Download aria-hidden />}
            {busy === "download" ? "Đang chuẩn bị file…" : what === "excel" ? `Tải Excel ${n || ""} mã` : `Tải ${n || ""} vận đơn`}
          </Button>
          {what !== "excel" && (
            <Button onClick={print} disabled={downloading || count.isLoading || n === 0}>
              {busy === "print" ? <Loader2 className="animate-spin" aria-hidden /> : <Printer aria-hidden />}
              {busy === "print" ? "Đang chuẩn bị bản in…" : `In ${n || ""} vận đơn`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
